import { z } from "zod";
import { TimesheetEntryBaseWageType } from "@api/shared-runtime/enums";

// Re-export the enum for convenience
export { TimesheetEntryBaseWageType };

// Re-export timezone utilities
export {
  getTimezoneLabel,
  getDateInTimezone,
  getTimeInTimezone,
  combineDateTimeInTimezone,
  formatDateInTimezone,
  formatTimeInTimezone,
  getAvailableDates,
  type TimeZone,
} from "./timezone-utils";

/**
 * Schema for a single time block within a shift.
 * Uses full ISO datetime strings to support multi-day shifts.
 */
export const timeBlockSchema = z
  .object({
    startsAt: z.string().min(1, "Start time is required"), // ISO datetime string
    endsAt: z.string().min(1, "End time is required"), // ISO datetime string
    baseWageType: z.nativeEnum(TimesheetEntryBaseWageType),
    isOvertime: z.boolean(),
    providerNote: z.string().optional(),
  })
  .refine(
    (data) => {
      if (!data.startsAt || !data.endsAt) {
        return true;
      }
      return new Date(data.endsAt) > new Date(data.startsAt);
    },
    {
      message: "End time must be after start time",
    },
  );

export type TimeBlockFormData = z.infer<typeof timeBlockSchema>;

/**
 * Schema for a single shift's time entries.
 */
export const shiftTimeEntrySchema = z.object({
  shiftId: z.string().uuid(),
  shiftDate: z.date(), // For display and date calculations
  timeBlocks: z
    .array(timeBlockSchema)
    .min(1, "At least one time block is required"),
});

export type ShiftTimeEntryFormData = z.infer<typeof shiftTimeEntrySchema>;

/**
 * Schema for the entire weekly time entry form.
 */
export const weeklyTimeEntryFormSchema = z.object({
  weekKey: z.string(),
  shifts: z.array(shiftTimeEntrySchema),
});

export type WeeklyTimeEntryFormData = z.infer<typeof weeklyTimeEntryFormSchema>;

/**
 * Create default time block values based on scheduled shift times.
 */
export function createDefaultTimeBlock(
  scheduledStartsAt: Date,
  scheduledEndsAt: Date,
  suggestedWageType: TimesheetEntryBaseWageType,
  isOvertime = false,
): TimeBlockFormData {
  return {
    startsAt: scheduledStartsAt.toISOString(),
    endsAt: scheduledEndsAt.toISOString(),
    baseWageType: suggestedWageType,
    isOvertime,
    providerNote: "",
  };
}

/**
 * Create an overtime time block starting from the previous block's end time.
 */
export function createOvertimeTimeBlock(
  suggestedWageType: TimesheetEntryBaseWageType,
  baseDate: Date,
  previousEndTime?: Date,
): TimeBlockFormData {
  // Start at the previous block's end time, or beginning of shift day
  const startTime = previousEndTime || baseDate;

  return {
    startsAt: startTime.toISOString(),
    endsAt: startTime.toISOString(), // Same as start - user will adjust
    baseWageType: suggestedWageType,
    isOvertime: true,
    providerNote: "",
  };
}

/**
 * Parse date part from ISO datetime string (YYYY-MM-DD) in local timezone.
 */
export function getDateFromISO(isoString: string): string {
  if (!isoString) {
    return "";
  }
  const date = new Date(isoString);
  // Use local date components, not UTC
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parse time part from ISO datetime string (HH:mm) in local timezone.
 */
export function getTimeFromISO(isoString: string): string {
  if (!isoString) {
    return "";
  }
  const date = new Date(isoString);
  return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
}

/**
 * Combine date (YYYY-MM-DD) and time (HH:mm) into ISO datetime string.
 */
export function combineDateAndTime(dateStr: string, timeStr: string): string {
  if (!dateStr || !timeStr) {
    return "";
  }
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes] = timeStr.split(":").map(Number);
  // Create date using local timezone
  const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
  return date.toISOString();
}

/**
 * Format base wage type for display.
 */
export function formatWageType(wageType: TimesheetEntryBaseWageType): string {
  const labels: Record<TimesheetEntryBaseWageType, string> = {
    [TimesheetEntryBaseWageType.Regular]: "Regular",
    [TimesheetEntryBaseWageType.OnCall]: "On Call",
    [TimesheetEntryBaseWageType.Weekend]: "Weekend",
    [TimesheetEntryBaseWageType.Holiday]: "Holiday",
    [TimesheetEntryBaseWageType.OnCallCallback]: "On Call Callback",
  };
  return labels[wageType] || wageType;
}

/**
 * Calculate duration in hours from ISO datetime strings.
 */
export function calculateDurationHours(
  startsAt: string,
  endsAt: string,
): number {
  if (!startsAt || !endsAt) {
    return 0;
  }

  const start = new Date(startsAt);
  const end = new Date(endsAt);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }

  const diffMs = end.getTime() - start.getTime();
  return diffMs / (1000 * 60 * 60);
}

/**
 * Format duration for display.
 */
export function formatDuration(hours: number): string {
  if (hours <= 0) {
    return "";
  }

  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  if (minutes === 0) {
    return `${wholeHours}h`;
  }

  return `${wholeHours}h ${minutes}m`;
}
